import { returnPolicyPropertiesFromDataset } from "../helpers/returnPolicyProperties";
import {
  cartItemTracking,
  sourceFromDataset,
  trackGiftNoteSaved,
} from "../helpers/dataLayer";

class CartGifting extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.conditionalDetails = this.querySelector("conditional-details");

    this.giftBoxForm = this.querySelector("[data-gift-box-form]");
    this.noteToggle = this.querySelector("[data-gift-note-toggle]");
    this.noteContainer = this.querySelector("[data-gift-note]");
    this.noteInput = this.querySelector("[data-gift-note-input]");
    this.noteCount = this.querySelector("[data-gift-note-count]");
    this.noteSave = this.querySelector("[data-gift-note-save]");
    this.needBefore = this.querySelector("[data-need-before]");

    // What the cart currently holds — the Save button stays disabled until the
    // textarea diverges from it.
    this.savedNote = this.noteInput.value;

    this.onGiftBoxSubmit = this.onGiftBoxSubmit.bind(this);
    this.onNoteToggle = this.onNoteToggle.bind(this);
    this.onNoteInput = this.onNoteInput.bind(this);
    this.onNoteSave = this.onNoteSave.bind(this);
    this.onNeedBeforeChange = this.onNeedBeforeChange.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    this.giftBoxForm?.addEventListener("submit", this.onGiftBoxSubmit);
    this.noteToggle.addEventListener("change", this.onNoteToggle);
    this.noteInput.addEventListener("input", this.onNoteInput);
    this.noteSave.addEventListener("click", this.onNoteSave);
    this.needBefore?.addEventListener("change", this.onNeedBeforeChange);
    document.addEventListener("updateCart", this.onCartUpdate);

    this.setNoteOpen(this.noteToggle.checked);

    if (
      this.hasAttribute("data-open-on-desktop") &&
      window.matchMedia(
        `(min-width: ${window.themeBreakpoints["tablet-wide"]}px)`,
      ).matches
    ) {
      // Set on the <details> directly rather than via conditional-details,
      // which may not have upgraded yet.
      const details = this.querySelector("details");
      details.open = true;
      details.classList.add("open");
    }
  }

  disconnectedCallback() {
    this.initialized = false;
    this.giftBoxForm?.removeEventListener("submit", this.onGiftBoxSubmit);
    this.noteToggle?.removeEventListener("change", this.onNoteToggle);
    this.noteInput?.removeEventListener("input", this.onNoteInput);
    this.noteSave?.removeEventListener("click", this.onNoteSave);
    this.needBefore?.removeEventListener("change", this.onNeedBeforeChange);
    document.removeEventListener("updateCart", this.onCartUpdate);
  }

  // The cart morph skips this subtree (data-attributes-only) so local UI state —
  // the open accordion, focus, a half-typed note — survives. The trade-off is
  // that the drawer's copy and the cart page's copy have to reconcile themselves
  // from the cart payload every update.
  onCartUpdate(e) {
    // The instance that made the change is already settling via its own
    // onAfterAdd; syncing it here would just fight that.
    if (e.detail.data?.source === this) return;

    this.syncFromCart(e.detail.cart);
  }

  syncFromCart(cart) {
    if (!cart) return;

    if (this.needBefore) {
      this.needBefore.checked = cart.note === this.needBefore.value;
    }

    const note = cart.attributes?.["_gift-note"] || "";
    if (note === this.savedNote) return;

    // A note being typed here outranks the incoming one — adopting the new
    // saved value is enough to put Save back in the right state.
    const hasUnsavedEdits = this.noteInput.value !== this.savedNote;

    this.savedNote = note;

    if (!hasUnsavedEdits) {
      this.noteInput.value = note;
      this.setNoteOpen(Boolean(note));
    }

    this.onNoteInput();
  }

  setNoteOpen(open) {
    this.noteToggle.checked = open;
    this.classList.toggle("cart-gifting--note-open", open);
  }

  onGiftBoxSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.giftBoxForm);
    const quantity = parseInt(formData.get("quantity"), 10);

    window.addToCart(
      {
        id: formData.get("id"),
        quantity: isNaN(quantity) ? 1 : quantity,
        properties: returnPolicyPropertiesFromDataset(this.giftBoxForm.dataset),
        tracking: cartItemTracking(sourceFromDataset(this.giftBoxForm.dataset)),
      },
      {
        onError: (response) =>
          this.showError(
            this.giftBoxForm,
            response?.message || window.localeStrings.product.add_to_cart_error,
          ),
        onAfterAdd: () => {
          this.giftBoxForm.reset();
          this.giftBoxForm
            .querySelector("[name='quantity']")
            ?.dispatchEvent(new Event("change", { bubbles: true }));
          this.conditionalDetails?.close();
        },
      },
    );
  }

  onNoteToggle() {
    this.setNoteOpen(this.noteToggle.checked);

    if (this.noteToggle.checked) {
      // The textarea is still clipped to nothing at this point, so a plain
      // focus() would scroll it into view and yank the reveal open.
      this.noteInput.focus({ preventScroll: true });
      return;
    }

    // Unchecking drops the note from the cart, so the checkbox always reflects
    // whether one is attached.
    this.noteInput.value = "";
    this.onNoteInput();

    if (this.savedNote) this.saveNote();
  }

  onNoteInput() {
    const max = parseInt(this.dataset.noteMaxLength, 10);
    const remaining = max - this.noteInput.value.length;

    this.noteCount.textContent = this.dataset.charactersLeftTemplate.replace(
      "[count]",
      remaining,
    );

    this.updateSaveButton();
  }

  updateSaveButton() {
    const inSync = this.noteInput.value === this.savedNote;

    this.noteSave.disabled = inSync;
    // A note the shopper has only just enabled is in sync too — but there is
    // nothing saved yet, so it reads as Save rather than Saved.
    this.noteSave.textContent =
      inSync && this.savedNote ? this.dataset.savedText : this.dataset.saveText;
  }

  onNoteSave() {
    this.saveNote();
  }

  saveNote() {
    const note = this.noteInput.value;

    this.noteSave.disabled = true;
    this.noteSave.textContent = this.dataset.savingText;

    window.updateCart(
      { attributes: { "_gift-note": note } },
      {
        data: { source: this },
        onError: (response) => {
          this.updateSaveButton();
          this.showError(
            this.noteContainer,
            response?.message || this.dataset.saveError,
          );
        },
        onAfterAdd: () => {
          this.savedNote = note;
          this.updateSaveButton();
          trackGiftNoteSaved();
        },
      },
    );
  }

  onNeedBeforeChange() {
    window.updateCart(
      { note: this.needBefore.checked ? this.needBefore.value : "" },
      {
        data: { source: this },
        onError: () => {
          this.needBefore.checked = !this.needBefore.checked;
        },
      },
    );
  }

  showError(target, message) {
    const error = document.createElement("div");
    error.classList.add("add-to-cart-error");
    error.innerHTML = message;

    target.append(error);

    setTimeout(() => error.remove(), 3000);
  }
}

if (!customElements.get("cart-gifting")) {
  customElements.define("cart-gifting", CartGifting);
}
